import React, { useState } from 'react';
import styled from 'styled-components';
import { PageHeader, Grid, GridItem } from '../../../styles/components';
import { SpacePageContainer, GlassContainer, GlowingPageTitle } from '../../../styles/components/CompaniesList';
import { useNotifications } from '../../../contexts/notification';
import { scadWorkshops as initialWorkshops } from '../../../data/mock/scadOffice/workshopsData';
import WorkshopsList from './WorkshopsList';
import WorkshopForm from './WorkshopForm';
import WorkshopDetails from './WorkshopDetails';

// Close button for modals
const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 110;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: rgba(99, 102, 241, 0.5);
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
    color: rgba(255, 255, 255, 1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const WorkshopsManagement = () => {
  // State for workshops data
  const [workshops, setWorkshops] = useState(initialWorkshops);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const { addNotification } = useNotifications();

  // Filter workshops based on search text and status
  const filteredWorkshops = workshops.filter(workshop => {
    const matchesText = workshop.title.toLowerCase().includes(filterText.toLowerCase()) ||
                        workshop.shortDescription.toLowerCase().includes(filterText.toLowerCase());
    const matchesStatus = filterStatus === 'all' || workshop.status === filterStatus;
    
    return matchesText && matchesStatus;
  });

  // Handle workshop selection
  const handleSelectWorkshop = (workshop) => {
    setSelectedWorkshop(workshop);
    setIsViewOpen(true);
    setIsFormOpen(false);
  };

  // Handle creating a new workshop
  const handleCreateWorkshop = () => {
    setSelectedWorkshop(null);
    setIsEditing(false);
    setIsFormOpen(true);
    setIsViewOpen(false);
  };

  // Handle editing a workshop
  const handleEditWorkshop = (workshop) => {
    setSelectedWorkshop(workshop);
    setIsEditing(true);
    setIsFormOpen(true);
    setIsViewOpen(false);
  };

  // Handle saving a workshop (create or update)
  const handleSaveWorkshop = (workshopData) => {
    if (isEditing) {
      // Update existing workshop
      const updatedWorkshops = workshops.map(workshop => 
        workshop.id === workshopData.id ? workshopData : workshop
      );
      setWorkshops(updatedWorkshops);
      addNotification({
        title: 'Success',
        message: `Workshop "${workshopData.title}" has been updated.`,
        type: 'success'
      });
    } else {
      // Create new workshop with a generated ID
      const newWorkshop = {
        ...workshopData,
        id: Math.max(...workshops.map(w => w.id), 0) + 1
      };
      setWorkshops([...workshops, newWorkshop]);
      addNotification({
        title: 'Success',
        message: `Workshop "${workshopData.title}" has been created.`,
        type: 'success'
      });
    }
    
    setIsFormOpen(false);
  };

  // Handle deleting a workshop
  const handleDeleteWorkshop = (workshopId) => {
    const workshopToDelete = workshops.find(w => w.id === workshopId);
    
    if (window.confirm(`Are you sure you want to delete the workshop "${workshopToDelete.title}"?`)) {
      const updatedWorkshops = workshops.filter(workshop => workshop.id !== workshopId);
      setWorkshops(updatedWorkshops);
      
      addNotification({
        title: 'Workshop Deleted',
        message: `Workshop "${workshopToDelete.title}" has been deleted.`,
        type: 'info'
      });
      
      if (isViewOpen && selectedWorkshop?.id === workshopId) {
        setIsViewOpen(false);
        setSelectedWorkshop(null);
      }
    }
  };

  // Handle closing the form or details view
  const handleClose = () => {
    setIsFormOpen(false);
    setIsViewOpen(false);
    setSelectedWorkshop(null);
  };

  return (
    <SpacePageContainer>
      <GlassContainer>
        <PageHeader>
          <GlowingPageTitle>Workshops Management</GlowingPageTitle>
        </PageHeader>
        
        <div className="workshop-content" style={{ position: "relative" }}>
          {/* Use a modal layout approach instead of grid columns for forms and details */}
          <WorkshopsList 
            workshops={filteredWorkshops}
            onCreateWorkshop={handleCreateWorkshop}
            onSelectWorkshop={handleSelectWorkshop}
            onEditWorkshop={handleEditWorkshop}
            onDeleteWorkshop={handleDeleteWorkshop}
            filterText={filterText}
            setFilterText={setFilterText}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
          />
          
          {isFormOpen && (
            <div className="modal-container" style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(15, 23, 42, 0.8)",
              backdropFilter: "blur(8px)",
              zIndex: 100,
              padding: "20px"
            }}>
              <CloseButton onClick={handleClose}>×</CloseButton>
              <div style={{ 
                width: "95%", 
                maxWidth: "750px",
                maxHeight: "85vh",
                overflow: "auto",
                borderRadius: "16px",
                boxShadow: "0 15px 50px rgba(0, 0, 0, 0.3)",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(99, 102, 241, 0.3) rgba(15, 23, 42, 0.2)",
                position: "relative"
              }}>
                <CloseButton onClick={handleClose}>×</CloseButton>
                <WorkshopForm 
                  workshop={isEditing ? selectedWorkshop : null}
                  isEditing={isEditing}
                  onSave={handleSaveWorkshop}
                  onCancel={handleClose}
                />
              </div>
            </div>
          )}
          
          {isViewOpen && selectedWorkshop && (
            <div className="modal-container" style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(15, 23, 42, 0.8)",
              backdropFilter: "blur(8px)",
              zIndex: 100,
              padding: "20px"
            }}>
              <CloseButton onClick={handleClose}>×</CloseButton>
              <div style={{ 
                width: "95%", 
                maxWidth: "750px",
                maxHeight: "85vh",
                overflow: "auto",
                borderRadius: "16px",
                boxShadow: "0 15px 50px rgba(0, 0, 0, 0.3)",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(99, 102, 241, 0.3) rgba(15, 23, 42, 0.2)"
              }}>
                <CloseButton onClick={handleClose}>×</CloseButton>
                <WorkshopDetails 
                  workshop={selectedWorkshop}
                  onEdit={() => handleEditWorkshop(selectedWorkshop)}
                  onDelete={() => handleDeleteWorkshop(selectedWorkshop.id)}
                  onClose={handleClose}
                />
              </div>
            </div>
          )}
        </div>
      </GlassContainer>
    </SpacePageContainer>
  );
};

export default WorkshopsManagement;